import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getEngagementDetail,
  getEngagementList,
  getInspectionList,
  getLoggedInUser,
  getInspectionCycle,
} from "../../helpers/backend_helper";
import { ErrorNotify } from "../../shared/Alert";
import "./EngagementPreviewPage.scss";

const displayDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const statusLabel = (inspection) => {
  if (inspection.abortedOn) return "Aborted";
  if (inspection.completedOn) return "Completed";
  return (
    inspection.inspectionStatusName ||
    (inspection.isLocked ? "In Progress" : "Not Started")
  );
};

const statusTone = (value) => {
  if (/complete|approved|closed/i.test(value)) return "complete";
  if (/progress|review|active/i.test(value)) return "progress";
  if (/abort|overdue|attention|return/i.test(value)) return "attention";
  return "new";
};

const detailValue = (value) => value || "—";

export default function EngagementPreviewPage() {
  const { engagementId } = useParams();
  const navigate = useNavigate();
  const [engagement, setEngagement] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadPreview = () => {
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      getEngagementDetail(engagementId),
      getInspectionList(),
      getEngagementList().catch(() => null),
      getLoggedInUser().catch(() => null),
    ])
      .then(
        async ([
          engagementResponse,
          inspectionResponse,
          engagementListResponse,
          account,
        ]) => {
          if (!engagementResponse?.status)
            throw new Error(
              engagementResponse?.message || "Unable to load the engagement."
            );
          if (!inspectionResponse?.status)
            throw new Error(
              inspectionResponse?.message || "Unable to load inspections."
            );

          const record = engagementResponse.data;
          const engagementInspections = Array.isArray(inspectionResponse.data)
            ? inspectionResponse.data.filter(
                (inspection) =>
                  Number(inspection.engagementId) === Number(engagementId)
              )
            : [];
          const listRecord =
            engagementListResponse?.status &&
            Array.isArray(engagementListResponse.data)
              ? engagementListResponse.data.find(
                  (item) => Number(item.id) === Number(engagementId)
                )
              : null;
          const cycleIds = [
            ...new Set(
              [
                ...(Array.isArray(record?.inspectionCycleIds)
                  ? record.inspectionCycleIds
                  : []),
                ...(Array.isArray(listRecord?.inspectionCycleIds)
                  ? listRecord.inspectionCycleIds
                  : []),
                record?.inspectionCycleId,
                listRecord?.inspectionCycleId,
                ...engagementInspections.map(
                  (inspection) => inspection.inspectionCycleId
                ),
              ]
                .map(Number)
                .filter(Boolean)
            ),
          ];
          const cycleResponses = await Promise.all(
            cycleIds.map((id) => getInspectionCycle(id).catch(() => null))
          );
          if (!active) return;

          const roleName =
            account?.role?.name ||
            account?.roleName ||
            account?.userRole?.name ||
            account?.designation ||
            "";
          setCanManage(
            Boolean(account) &&
              Number(account?.roleId) !== 4 &&
              !/engagement\s+member/i.test(roleName)
          );
          setEngagement(record);
          setInspections(engagementInspections);
          setCycles(
            cycleResponses
              .filter((response) => response?.status && response.data)
              .map((response) => response.data)
          );
        }
      )
      .catch((reason) => {
        if (!active) return;
        const message =
          reason?.message || "Unable to load the engagement preview.";
        setError(message);
        ErrorNotify(message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  };

  useEffect(loadPreview, [engagementId]);

  const clientName =
    engagement?.client?.clientName ||
    engagement?.client?.name ||
    engagement?.clientName ||
    "—";
  const primaryCycle = cycles[0];
  const cycleTitle =
    primaryCycle?.title ||
    primaryCycle?.projectTitle ||
    primaryCycle?.name ||
    "Unassigned";
  const engagementStatus =
    engagement?.engagementStatus?.name ||
    engagement?.engagementStatusName ||
    (engagement?.status === 1 ? "Active" : "Inactive");
  const completedCount = useMemo(
    () =>
      inspections.filter((inspection) =>
        /complete/i.test(statusLabel(inspection))
      ).length,
    [inspections]
  );
  const activeCount = inspections.length - completedCount;

  if (loading) {
    return (
      <main className="page-content engagement-preview piers-page">
        <div className="engagement-preview__state">
          <span className="spinner-border text-primary" aria-hidden="true" />
          <b>Loading engagement preview…</b>
        </div>
      </main>
    );
  }

  if (error || !engagement) {
    return (
      <main className="page-content engagement-preview piers-page">
        <div className="engagement-preview__state is-error">
          <i className="bx bx-error-circle" />
          <h2>Engagement preview unavailable</h2>
          <p>{error || "The engagement could not be found."}</p>
          <button type="button" onClick={() => navigate("/engagements/list")}>
            Back to Engagements
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="page-content engagement-preview piers-page">
      <button
        type="button"
        className="engagement-preview__back"
        onClick={() => navigate("/engagements/list")}
      >
        <i className="bx bx-left-arrow-alt" /> Engagements
      </button>

      <header className="engagement-preview__header piers-page-header">
        <div>
          <div className="engagement-preview__eyebrow">
            <span>{engagement.engagementNo || `ENG-${engagement.id}`}</span>
            <em className={`is-${statusTone(engagementStatus)}`}>
              {engagementStatus}
            </em>
          </div>
          <h1 className="piers-page-title">{engagement.name}</h1>
          <p>
            Review the engagement details and all inspections created for this
            engagement.
          </p>
        </div>
        <div className="engagement-preview__actions">
          <button className="piers-refresh-button" type="button" onClick={loadPreview}>
            <i className="bx bx-refresh" /> Refresh
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => navigate(`/engagements/${engagement.id}/edit`)}
            >
              <i className="bx bx-pencil" /> Edit Engagement
            </button>
          )}
        </div>
      </header>

      <section
        className="engagement-preview__summary"
        aria-label="Engagement summary"
      >
        <a className="engagement-preview__summary-card is-blue" href="#engagement-inspection-reviews">
          <i className="bx bx-check-square" />
          <div>
            <span>Inspection Reviews</span>
            <strong>{inspections.length}</strong>
            <small>
              {activeCount} active · {completedCount} completed
            </small>
          </div>
        </a>
        <article className="is-green">
          {primaryCycle && (
            <Link
              className="engagement-preview__card-link"
              to={`/inspections/${primaryCycle.id}/preview`}
              state={{ fromPath: `/engagements/${engagementId}/preview` }}
              aria-label="View inspection cycle"
            />
          )}
          <i className="bx bx-calendar" />
          <div>
            <span>Inspection Cycles</span>
            {primaryCycle ? (
              <strong>
                {cycles.length > 1
                  ? `${cycles.length} assigned cycles`
                  : cycleTitle}
              </strong>
            ) : (
              <strong>Unassigned</strong>
            )}
            <small>{primaryCycle?.projectNo || "Cycle assignment"}</small>
          </div>
        </article>
        <article className="is-purple">
          <i className="bx bx-buildings" />
          <div>
            <span>Client / Firm</span>
            <strong>{clientName}</strong>
            <small>{engagement.clientType?.name || "Client"}</small>
          </div>
        </article>
        <article className="is-orange">
          <i className="bx bx-briefcase-alt-2" />
          <div>
            <span>Work Type</span>
            <strong>{engagement.workType?.name || "—"}</strong>
            <small>
              {engagement.industryType?.name || "Industry not specified"}
            </small>
          </div>
        </article>
      </section>

      <section className="engagement-preview__details">
        <header>
          <div>
            <h2>Engagement Details</h2>
            <p>Core information recorded for this engagement.</p>
          </div>
        </header>
        <dl>
          <div>
            <dt>Engagement ID</dt>
            <dd>{engagement.engagementNo || `ENG-${engagement.id}`}</dd>
          </div>
          <div>
            <dt>Engagement Name</dt>
            <dd>{engagement.name}</dd>
          </div>
          <div>
            <dt>Client / Firm</dt>
            <dd>{clientName}</dd>
          </div>
          <div>
            <dt>Client Type</dt>
            <dd>{detailValue(engagement.clientType?.name)}</dd>
          </div>
          <div>
            <dt>Work Type</dt>
            <dd>{detailValue(engagement.workType?.name)}</dd>
          </div>
          <div>
            <dt>Industry Type</dt>
            <dd>{detailValue(engagement.industryType?.name)}</dd>
          </div>
          <div>
            <dt>Financial Year End</dt>
            <dd>{detailValue(engagement.finYearEnd)}</dd>
          </div>
          <div>
            <dt>Preparation Owner</dt>
            <dd>{detailValue(engagement.assignedUser?.fullName)}</dd>
          </div>
          <div>
            <dt>Due Date</dt>
            <dd>{displayDate(engagement.dueDate)}</dd>
          </div>
          <div>
            <dt>Progress</dt>
            <dd>{Number(engagement.percentage) || 0}%</dd>
          </div>
        </dl>
        {(engagement.description || engagement.scopeDescription) && (
          <div className="engagement-preview__description">
            <h3>Description & Scope</h3>
            <p>{engagement.description || engagement.scopeDescription}</p>
          </div>
        )}
      </section>

      <section id="engagement-inspection-reviews" className="engagement-preview__inspections">
        <header>
          <div>
            <h2>
              Inspection Reviews <span>{inspections.length}</span>
            </h2>
            <p>Reviews created from this engagement's cycle configurations.</p>
          </div>
        </header>
        {inspections.length ? (
          <div className="engagement-preview__table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Inspection ID</th>
                  <th>Review Type</th>
                  <th>Inspection Cycle</th>
                  <th>Owner</th>
                  <th>Assigned Date</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {inspections.map((inspection) => {
                  const label = statusLabel(inspection);
                  const cycle = cycles.find((item) => Number(item.id) === Number(inspection.inspectionCycleId));
                  return (
                    <tr key={inspection.id}>
                      <td>
                        <strong>
                          {inspection.inspectionNo || `INS-${inspection.id}`}
                        </strong>
                      </td>
                      <td>{inspection.inspectionType?.name || inspection.inspectionTypeName || "—"}</td>
                      <td>
                        {Number(inspection.inspectionCycleId) > 0 ? (
                          <Link to={`/inspections/${inspection.inspectionCycleId}/preview`} state={{ fromPath: `/engagements/${engagementId}/preview` }}>
                            {cycle?.projectNo || `IC-${inspection.inspectionCycleId}`}
                            {cycle?.title || cycle?.projectTitle ? ` — ${cycle.title || cycle.projectTitle}` : ""}
                          </Link>
                        ) : "Unassigned"}
                      </td>
                      <td>{inspection.ownerUser?.fullName || inspection.ownerName || inspection.assignedUser?.fullName || "Unassigned"}</td>
                      <td>{displayDate(inspection.assignedDate)}</td>
                      <td>
                        <span
                          className={`engagement-preview__status is-${statusTone(
                            label
                          )}`}
                        >
                          {label}
                        </span>
                      </td>
                      <td>
                        <div className="engagement-preview__progress">
                          <span>
                            <i
                              style={{
                                width: `${Math.min(
                                  100,
                                  Math.max(
                                    0,
                                    Number(inspection.percentage) || 0
                                  )
                                )}%`,
                              }}
                            />
                          </span>
                          <b>{Number(inspection.percentage) || 0}%</b>
                        </div>
                      </td>
                      <td>
                        <Link to={`/inspectionreview/${inspection.id}`} state={{ engagementReturnPath: `/engagements/${engagementId}/preview` }}>
                          Open <i className="bx bx-right-arrow-alt" aria-hidden="true" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="engagement-preview__empty">
            <span>
              <i className="bx bx-check-square" />
            </span>
            <h3>No inspection reviews created</h3>
            <p>
              {canManage
                ? "Attach this engagement to an inspection cycle and complete its review configuration."
                : "Reviews assigned to this engagement will appear here."}
            </p>
          </div>
        )}
      </section>
    </main>
  );
}
